import { skedInboxApi } from "./_skedInboxApi";
import type {
  InboxV2RawConversationsResponse,
  InboxV2RawMessage,
} from "../@inbox-v2/data/real-api/types";

type InboxV2AccountFilterDto =
  { match: "all" } | { match: "or"; values: string[] };
type InboxV2MatchFilterDto<T extends string = string> =
  { match: "all" } | { match: "or"; values: T[] };
type InboxV2AccessDto = "organizational" | "personal";
type InboxV2FeedStatusFilterDto = "closed" | "open";
type InboxV2MessageTypeDto =
  | "comment"
  | "threaded-comment"
  | "dm"
  | "mention"
  | "mention-in-caption"
  | "post"
  | "tagged-post"
  | "tagged-in-media"
  | "review"
  | "qa";
type InboxV2SavedFilterDto = "saved" | "not-saved";
type InboxV2MentionedFilterDto = "mentioned";
type InboxV2AssigneeFilterDto = string;
type InboxV2SentimentDto =
  "positive" | "negative" | "neutral" | "mixed" | "unknown";
type InboxV2PlatformDto = "IG" | "FB" | "TT" | "LI" | "GMB" | "SK" | "TW";
export type InboxV2TemplateTypeDto = "static" | "ai";
export type InboxV2TemplateAccessScopeDto =
  "organization" | "personal" | "board";
export type InboxV2TemplateMessageTypeDto =
  "comment" | "direct-message" | "mention" | "post" | "review" | "qa";
export type InboxV2TemplateChannelDto =
  "facebook" | "instagram" | "linkedin" | "tiktok" | "google-business";

type InboxV2BoardDto = {
  id: string;
  name: string;
  access: InboxV2AccessDto;
  createdByUserId: string;
  pinned: boolean;
  feedIds: string[];
};

type InboxV2FeedDto = {
  id: string;
  boardId: string;
  name: string;
  access: InboxV2AccessDto;
  createdByUserId: string;
  hidden: boolean;
  filters: {
    accountIds: InboxV2AccountFilterDto;
    conversationIds?: InboxV2MatchFilterDto<string> | null;
    messageType: InboxV2MatchFilterDto<InboxV2MessageTypeDto>;
    status: InboxV2MatchFilterDto<InboxV2FeedStatusFilterDto>;
    postType: InboxV2MatchFilterDto;
    sentiment: InboxV2MatchFilterDto<InboxV2SentimentDto>;
    assignee?: InboxV2MatchFilterDto<InboxV2AssigneeFilterDto>;
    tags?: InboxV2MatchFilterDto<string>;
    mentioned?: InboxV2MatchFilterDto<InboxV2MentionedFilterDto>;
    dateRange?: {
      dateFrom: string | null;
      dateTo: string | null;
    };
    messageMatch?: {
      messageType?: InboxV2MatchFilterDto<InboxV2MessageTypeDto>;
      sentiment?: InboxV2MatchFilterDto<InboxV2SentimentDto>;
      dateRange?: {
        dateFrom: string | null;
        dateTo: string | null;
      };
    };
    saved?: InboxV2MatchFilterDto<InboxV2SavedFilterDto>;
  };
};

type InboxV2RefreshPostPreviewsResponse = {
  items: InboxV2RawConversationsResponse["items"];
  errors?: Array<{
    conversationId: string;
    postId: string;
    fetchError?: InboxV2PostCommentsFetchErrorDto;
  }>;
};
type InboxV2RefreshMessageAttachmentsResponse = {
  items: Array<{
    conversationId: string;
    message: InboxV2RawMessage;
  }>;
  errors?: Array<{
    messageId: string;
    fetchError?: InboxV2PostCommentsFetchErrorDto;
  }>;
};
export type InboxV2CheckMessageFileAttachmentsResponse = {
  items: Array<{
    messageId: string;
    refreshRecommended: boolean;
    status: "expired" | "unknown" | "valid";
  }>;
  errors: Array<{
    messageId: string;
    message: string;
  }>;
};

export type InboxV2BulkUpdateConversationsResponse = {
  results: Array<
    | {
        conversationIds: string;
        state: "updated";
        systemMessages: InboxV2RawMessage[];
      }
    | {
        conversationIds: string;
        state: "ignored";
      }
  >;
};

export type InboxV2BulkAssignConversationsResponse =
  InboxV2BulkUpdateConversationsResponse;
export type InboxV2BulkUnassignConversationsResponse =
  InboxV2BulkUpdateConversationsResponse;
export type InboxV2BulkUpdateStarredResponse =
  InboxV2BulkUpdateConversationsResponse;
export type InboxV2BulkUpdateStatusResponse =
  InboxV2BulkUpdateConversationsResponse;

export type InboxV2SenderProfileDto = {
  id: string;
  platform: InboxV2PlatformDto;
  platformId: string;
  name: string;
  image?: string | null;
  handle: string;
  followersCount?: number;
  followingCount?: number;
  followingHim?: boolean;
  followingMe?: boolean;
  postsCount?: number;
  updatedAt?: string;
  refreshError?: InboxV2SenderProfileRefreshErrorDto;
};

export type InboxV2SenderProfileRefreshErrorDto = {
  name?: string;
  message: string;
  code?: string | number;
  status?: number;
  details?: unknown;
};

export type InboxV2PostCommentDto = {
  id: string;
  authorName: string;
  authorHandle: string;
  avatarUrl?: string;
  body: string;
  conversationId: string;
  createdAt: string;
  isCurrentConversation?: boolean;
  likeCount: number;
  replyCount: number;
};

export type InboxV2ProfileCommentDto = {
  id: string;
  body: string;
  conversationId: string;
  createdAt: string;
};

export type InboxV2PostCommentsFetchErrorDto = {
  name?: string;
  message: string;
  code?: string | number;
  status?: number;
  details?: unknown;
};

export type InboxV2PostCommentReplyDto = Omit<
  InboxV2PostCommentDto,
  "conversationId" | "isCurrentConversation" | "replyCount"
>;

export type InboxV2TemplateDto = {
  id: string;
  shortcut: string;
  templateType: InboxV2TemplateTypeDto;
  body: string;
  access: {
    scope: InboxV2TemplateAccessScopeDto;
    boardId?: string;
  };
  preferredUsage: {
    messageTypes: InboxV2TemplateMessageTypeDto[];
    channels: InboxV2TemplateChannelDto[];
  };
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
};

export type InboxV2TemplateMutationInput = {
  shortcut: string;
  templateType?: InboxV2TemplateTypeDto;
  body: string;
  access?: InboxV2TemplateDto["access"];
  preferredUsage?: Partial<InboxV2TemplateDto["preferredUsage"]>;
};

export async function getInboxV2Boards() {
  const { data } = await skedInboxApi.get<{
    boards: InboxV2BoardDto[];
    feeds: InboxV2FeedDto[];
    hasLimitedAccountAccess: boolean;
  }>("/inbox-v2/boards");

  return data;
}

export async function getInboxV2Templates() {
  const { data } = await skedInboxApi.get<{
    templates: InboxV2TemplateDto[];
  }>("/inbox-v2/templates");

  return data;
}

export async function createInboxV2Template(
  input: InboxV2TemplateMutationInput
) {
  const { data } = await skedInboxApi.post<InboxV2TemplateDto>(
    "/inbox-v2/templates",
    input
  );

  return data;
}

export async function updateInboxV2Template(
  input: { id: string } & InboxV2TemplateMutationInput
) {
  const { id, ...payload } = input;
  const { data } = await skedInboxApi.patch<InboxV2TemplateDto>(
    `/inbox-v2/templates/${id}`,
    payload
  );

  return data;
}

export async function deleteInboxV2Template(templateId: string) {
  await skedInboxApi.delete(`/inbox-v2/templates/${templateId}`);
}

export async function generateInboxV2TemplateReply(input: {
  lastMessage: string;
  postCaption?: string;
  prompt: string;
}) {
  const { data } = await skedInboxApi.post<{ response: string }>(
    "/inbox-v2/templates/generate-reply",
    input
  );

  return data;
}

export async function createInboxV2Board(input: {
  name: string;
  access?: InboxV2BoardDto["access"];
}) {
  const { data } = await skedInboxApi.post<InboxV2BoardDto>(
    "/inbox-v2/boards",
    input
  );
  return data;
}

export async function updateInboxV2Board(input: {
  id: string;
  name: string;
  access: InboxV2BoardDto["access"];
}) {
  const { data } = await skedInboxApi.patch<InboxV2BoardDto>(
    `/inbox-v2/boards/${input.id}`,
    { access: input.access, name: input.name }
  );
  return data;
}

export async function setInboxV2BoardPinned(input: {
  boardId: string;
  pinned: boolean;
}) {
  const { data } = await skedInboxApi.patch<InboxV2BoardDto>(
    `/inbox-v2/boards/${input.boardId}/pinned`,
    { pinned: input.pinned }
  );
  return data;
}

export async function deleteInboxV2Board(boardId: string) {
  await skedInboxApi.delete(`/inbox-v2/boards/${boardId}`);
}

export async function updateInboxV2BoardFeedOrder(input: {
  boardId: string;
  feedIds: string[];
}) {
  await skedInboxApi.patch(`/inbox-v2/boards/${input.boardId}/feed-order`, {
    feedIds: input.feedIds,
  });
}

export async function createInboxV2Feed(input: {
  boardId: string;
  name: string;
  accountIds: InboxV2AccountFilterDto;
  conversationIds?: InboxV2FeedDto["filters"]["conversationIds"];
  messageType: InboxV2FeedDto["filters"]["messageType"];
  status: InboxV2FeedDto["filters"]["status"];
  postType: InboxV2MatchFilterDto;
  sentiment: InboxV2FeedDto["filters"]["sentiment"];
  access: InboxV2FeedDto["access"];
  assignee?: InboxV2FeedDto["filters"]["assignee"];
  tags?: InboxV2FeedDto["filters"]["tags"];
  mentioned?: InboxV2FeedDto["filters"]["mentioned"];
  dateRange?: InboxV2FeedDto["filters"]["dateRange"];
  messageMatch?: InboxV2FeedDto["filters"]["messageMatch"];
  saved?: InboxV2FeedDto["filters"]["saved"];
}) {
  const { data } = await skedInboxApi.post<InboxV2FeedDto>(
    "/inbox-v2/feeds",
    normalizeFeedMutationPayload(input)
  );
  return data;
}

export async function updateInboxV2Feed(input: {
  id: string;
  boardId: string;
  name: string;
  accountIds: InboxV2AccountFilterDto;
  conversationIds?: InboxV2FeedDto["filters"]["conversationIds"];
  messageType: InboxV2FeedDto["filters"]["messageType"];
  status: InboxV2FeedDto["filters"]["status"];
  postType: InboxV2MatchFilterDto;
  sentiment: InboxV2FeedDto["filters"]["sentiment"];
  access: InboxV2FeedDto["access"];
  assignee?: InboxV2FeedDto["filters"]["assignee"];
  tags?: InboxV2FeedDto["filters"]["tags"];
  mentioned?: InboxV2FeedDto["filters"]["mentioned"];
  dateRange?: InboxV2FeedDto["filters"]["dateRange"];
  messageMatch?: InboxV2FeedDto["filters"]["messageMatch"];
  saved?: InboxV2FeedDto["filters"]["saved"];
}) {
  const { data } = await skedInboxApi.patch<InboxV2FeedDto>(
    `/inbox-v2/feeds/${input.id}`,
    normalizeFeedMutationPayload(input)
  );
  return data;
}

export async function deleteInboxV2Feed(feedId: string) {
  await skedInboxApi.delete(`/inbox-v2/feeds/${feedId}`);
}

export async function duplicateInboxV2Feed(feedId: string) {
  const { data } = await skedInboxApi.post<InboxV2FeedDto>(
    `/inbox-v2/feeds/${feedId}/duplicate`
  );
  return data;
}

export async function setInboxV2FeedHidden(feedId: string, hidden: boolean) {
  const { data } = await skedInboxApi.patch<InboxV2FeedDto>(
    `/inbox-v2/feeds/${feedId}/hidden`,
    { hidden }
  );
  return data;
}

export async function getInboxV2FeedConversations(input: {
  feedId: string;
  next?: string;
  previous?: string;
  limit?: number;
  messageLimit?: number;
}) {
  const { data } = await skedInboxApi.get<InboxV2RawConversationsResponse>(
    `/inbox-v2/feeds/${input.feedId}/conversations`,
    {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
      params: {
        next: input.next,
        previous: input.previous,
        limit: input.limit,
        messageLimit: input.messageLimit,
      },
    }
  );

  return data;
}

export async function getInboxV2ConversationsByFilters(input: {
  filters: InboxV2FeedDto["filters"];
  next?: string;
  previous?: string;
  limit?: number;
  messageLimit?: number;
}) {
  const { data } = await skedInboxApi.post<InboxV2RawConversationsResponse>(
    "/inbox-v2/conversations/filter",
    {
      filters: normalizeFeedFiltersPayload(input.filters),
      next: input.next,
      previous: input.previous,
      limit: input.limit,
      messageLimit: input.messageLimit,
    },
    {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
    }
  );

  return data;
}

export async function bulkUpdateInboxV2ConversationUnreadCounts(input: {
  conversationIds: string[];
  unreadCount: number;
}) {
  const { data } =
    await skedInboxApi.patch<InboxV2BulkUpdateConversationsResponse>(
      "/inbox-v2/conversations/unread-counts",
      input
    );

  return data;
}

export async function bulkUpdateInboxV2ConversationStarred(input: {
  conversationIds: string[];
  isStarred: boolean;
}) {
  const { data } = await skedInboxApi.patch<InboxV2BulkUpdateStarredResponse>(
    "/inbox-v2/conversations/starred",
    input
  );

  return data;
}

export async function bulkUpdateInboxV2ConversationStatuses(input: {
  conversationIds: string[];
  status: "OPEN" | "CLOSED";
}) {
  const { data } = await skedInboxApi.patch<InboxV2BulkUpdateStatusResponse>(
    "/inbox-v2/conversations/status",
    input
  );

  return data;
}

export async function bulkAssignInboxV2Conversations(input: {
  assigneeId: string;
  conversationIds: string[];
}) {
  const { data } =
    await skedInboxApi.patch<InboxV2BulkAssignConversationsResponse>(
      "/inbox-v2/conversations/assign",
      input
    );

  return data;
}

export async function bulkUnassignInboxV2Conversations(input: {
  conversationIds: string[];
}) {
  const { data } =
    await skedInboxApi.patch<InboxV2BulkUnassignConversationsResponse>(
      "/inbox-v2/conversations/unassign",
      input
    );

  return data;
}

function normalizeFeedMutationPayload<
  T extends {
    accountIds: InboxV2FeedDto["filters"]["accountIds"];
    messageType: InboxV2FeedDto["filters"]["messageType"];
    status: InboxV2FeedDto["filters"]["status"];
    postType: InboxV2FeedDto["filters"]["postType"];
    sentiment: InboxV2FeedDto["filters"]["sentiment"];
    access: InboxV2FeedDto["access"];
    assignee?: InboxV2FeedDto["filters"]["assignee"];
    tags?: InboxV2FeedDto["filters"]["tags"];
    mentioned?: InboxV2FeedDto["filters"]["mentioned"];
    dateRange?: InboxV2FeedDto["filters"]["dateRange"];
    messageMatch?: InboxV2FeedDto["filters"]["messageMatch"];
    saved?: InboxV2FeedDto["filters"]["saved"];
  },
>(input: T) {
  const { dateRange, messageMatch, ...rest } = input;

  return {
    ...rest,
    ...normalizeDateRangePayload(dateRange),
    ...normalizeMessageMatchPayload(messageMatch),
  };
}

function normalizeFeedFiltersPayload(filters: InboxV2FeedDto["filters"]) {
  const { dateRange, messageMatch, ...rest } = filters;

  return {
    ...rest,
    ...normalizeDateRangePayload(dateRange),
    ...normalizeMessageMatchPayload(messageMatch),
  };
}

function normalizeMessageMatchPayload(
  messageMatch?: InboxV2FeedDto["filters"]["messageMatch"]
) {
  if (!messageMatch) {
    return {};
  }

  const { dateRange, ...rest } = messageMatch;
  return {
    messageMatch: {
      ...rest,
      ...normalizeDateRangePayload(dateRange),
    },
  };
}

function normalizeDateRangePayload(
  dateRange?: InboxV2FeedDto["filters"]["dateRange"]
) {
  return dateRange?.dateFrom || dateRange?.dateTo ? { dateRange } : {};
}

export async function getInboxV2SenderProfiles(input: {
  identities: Array<{
    accountId: string;
    platform: string;
    platformId: string;
    postId?: string;
    messagePlatformId?: string;
  }>;
  refreshReason?: "image-load-error" | "fetched-error";
}) {
  const { data } = await skedInboxApi.post<{
    items: Array<{
      identity: {
        accountId: string;
        platform: string;
        platformId: string;
        postId?: string;
        messagePlatformId?: string;
      };
      profile: InboxV2SenderProfileDto;
    }>;
  }>(
    "/inbox-v2/sender-profiles",
    {
      identities: input.identities,
      refreshReason: input.refreshReason,
    },
    {
      headers: { "Cache-Control": "no-store", Pragma: "no-cache" },
    }
  );

  return data;
}

export async function refreshInboxV2MessageAttachments(input: {
  items: Array<{
    messageId: string;
  }>;
  refreshReason?: "image-load-error";
}) {
  const { data } =
    await skedInboxApi.post<InboxV2RefreshMessageAttachmentsResponse>(
      "/inbox-v2/messages/refresh",
      input,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache",
        },
      }
    );

  return data;
}

export async function checkInboxV2MessageFileAttachments(input: {
  items: Array<{
    messageId: string;
  }>;
}) {
  const { data } =
    await skedInboxApi.post<InboxV2CheckMessageFileAttachmentsResponse>(
      "/inbox-v2/messages/file-attachments/check",
      input,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache",
        },
      }
    );

  return data;
}

export async function uploadInboxV2MessageAttachment(input: {
  accountId: string;
  file: File;
  onUploadProgress?: (progress: number) => void;
}) {
  const { data } = await skedInboxApi.post<{
    attachment: {
      attachmentId: string;
      mimeType?: string;
      name?: string;
      size?: number;
      type: "image" | "video" | "audio" | "file";
    };
  }>(`/inbox-v2/accounts/${input.accountId}/message-attachments`, input.file, {
    headers: {
      "Content-Type": input.file.type || "application/octet-stream",
      "X-Inbox-V2-File-Name": input.file.name,
    },
    onUploadProgress: (event) => {
      if (!event.total) {
        return;
      }

      input.onUploadProgress?.(Math.round((event.loaded / event.total) * 100));
    },
  });

  return data.attachment;
}

export async function uploadInboxV2MessageAttachmentFromUrl(input: {
  accountId: string;
  mimeType?: string;
  name?: string;
  size?: number;
  type: "image" | "video" | "audio" | "file";
  url: string;
}) {
  const { accountId, ...body } = input;
  const { data } = await skedInboxApi.post<{
    attachment: {
      attachmentId: string;
      mimeType?: string;
      name?: string;
      size?: number;
      type: "image" | "video" | "audio" | "file";
    };
  }>(`/inbox-v2/accounts/${accountId}/message-attachments/from-url`, body);

  return data.attachment;
}

export async function getInboxV2PostComments(input: {
  conversationId: string;
  limit?: number;
  next?: string;
}) {
  const { data } = await skedInboxApi.get<{
    comments: InboxV2PostCommentDto[];
    fetchError?: InboxV2PostCommentsFetchErrorDto;
    nextToken?: string;
  }>(`/inbox-v2/conversations/${input.conversationId}/post-comments`, {
    headers: {
      "Cache-Control": "no-store",
      Pragma: "no-cache",
    },
    params: {
      limit: input.limit,
      next: input.next,
    },
  });

  return data;
}

export async function getInboxV2ProfileComments(input: {
  profileId: string;
  limit?: number;
  next?: string;
}) {
  const { data } = await skedInboxApi.get<{
    comments: InboxV2ProfileCommentDto[];
    nextToken?: string | null;
  }>(`/inbox-v2/profiles/${input.profileId}/comments`, {
    params: {
      limit: input.limit,
      next: input.next,
    },
  });

  return data;
}

export async function getInboxV2PostCommentReplies(input: {
  commentId: string;
  conversationId: string;
  limit?: number;
  next?: string;
}) {
  const { data } = await skedInboxApi.get<{
    fetchError?: InboxV2PostCommentsFetchErrorDto;
    replies: InboxV2PostCommentReplyDto[];
    nextToken?: string;
  }>(
    `/inbox-v2/conversations/${input.conversationId}/post-comments/${input.commentId}/replies`,
    {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
      params: {
        limit: input.limit,
        next: input.next,
      },
    }
  );

  return data;
}

export async function refreshInboxV2PostPreviews(input: {
  items: Array<{
    conversationId: string;
    postId: string;
  }>;
  refreshReason?: "image-load-error";
}) {
  const { data } = await skedInboxApi.post<InboxV2RefreshPostPreviewsResponse>(
    "/inbox-v2/posts/refresh",
    {
      items: input.items,
      refreshReason: input.refreshReason,
    },
    {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
    }
  );

  return data;
}
